import { isEqual } from "lodash";
import { useEffect } from "react";
import { useBusniessOfficeNotesContext } from "../../../context/busniessOfficeNotesContext";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { createBusniessOfficeNotesData } from "../utils/createBusniessOfficeNoteData";

/**
 * Syncs business-office notes from the Postgres-backed data-accumulator
 * context into the {@link useBusniessOfficeNotesContext} UI context.
 *
 * Re-runs whenever `generalOrderDetails` or `authOnFileDetails` change,
 * and skips the update when the order ID and raw notes are unchanged.
 *
 * The current `orgId` (via `getOrgIdForFetchExternalWorklist()`) is forwarded
 * to `createBusniessOfficeNotesData` so that org-specific overrides can apply
 * — e.g. Sunstate Urology keeps empty-date placeholder notes (`report_status`
 * + `template_text` only) instead of dropping them.
 *
 * When the order id or raw business-office notes change, clears
 * `writebackQcByReportId` so template-validation QC from a prior order or
 * snapshot cannot display under notes after navigation (provider is route-level).
 *
 * @returns void — operates purely through context setters as a side effect.
 */
export const useUpdateBusinessOfficeNotesContext = () => {
  const { generalOrderDetails, authOnFileDetails } =
    useDataAccumulatorForSingleOrder();

  const {
    setAllCommentsForUI,
    setOrderId,
    setAuthStatus,
    setManualAuthStatus,
    setRawData,
    setWritebackQcByReportId,
    rawData,
    orderId: busniessOfficeNotesOrderId,
  } = useBusniessOfficeNotesContext();

  useEffect(() => {
    if (!generalOrderDetails || !authOnFileDetails) return;

    const busniessOfficeNotes = authOnFileDetails?.auth_on_file?.reports ?? [];
    const isSameOrderId =
      busniessOfficeNotesOrderId === generalOrderDetails?.order?.order_id;
    const isSameBusniessOfficeNotes = isEqual(
      rawData ?? [],
      busniessOfficeNotes ?? [],
    );

    if (!isSameOrderId || !isSameBusniessOfficeNotes) {
      setWritebackQcByReportId({});
      const templateText = authOnFileDetails?.auth_on_file?.template_text ?? "";
      setAllCommentsForUI(
        createBusniessOfficeNotesData(
          busniessOfficeNotes,
          templateText,
          generalOrderDetails?.order?.payload?.date_of_service,
          getOrgIdForFetchExternalWorklist(),
        ),
      );
      setOrderId(generalOrderDetails?.order?.order_id ?? "");
      setAuthStatus(authOnFileDetails?.auth_on_file?.auth_status ?? "");
      if (!isSameOrderId) {
        setManualAuthStatus("");
      }
      setRawData(busniessOfficeNotes ?? []);
    }
  }, [
    generalOrderDetails,
    authOnFileDetails,
    busniessOfficeNotesOrderId,
    rawData,
    setWritebackQcByReportId,
  ]);
};
